import { useState } from 'react';
import ReactGA from 'react-ga4';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import api from '../api/api';
import { dataActions } from '../store/data';
import { flagActions } from '../store/flags';
import { themeActions } from '../store/theme';
import { errorHandler, setLocalData } from '../utils/helper';

interface ThemeConfig {
    primaryColor: string;
    logoUrl: string;
    favicon: string;
    title: string;
    description: string;
    loginOptions: Record<string, any>;
    isDark: boolean;
    supportPhone: string;
    supportEmail: string;
    websiteUrl: string;
    brokerAddress: string;
    legalName: string;
    mobileAppLinks: {
        ios: string;
        android: string;
        is_enabled: boolean;
    };
}

interface UseBrokerConfigReturn {
    loading: boolean;
    theme: ThemeConfig;
    customUrls: string[];
    fetchBrokerConfig: (organisation_uuid?: string) => void;
}

const useBrokerConfig = (): UseBrokerConfigReturn => {
    const dispatch = useDispatch();
    const location = useLocation();
    const userData = useSelector((state: any) => state.auth);

    const isSQ = location.pathname.includes('suraksha-qr');

    const [loading, setLoading] = useState(true);
    // const [ssoUrl, setSsoUrl] = useState<string | null>(null);
    const [customUrls, setCustomUrls] = useState<string[]>([]);
    const [theme, setTheme] = useState<ThemeConfig>({
        primaryColor: '',
        logoUrl: '',
        favicon: '',
        title: '',
        description: '',
        loginOptions: {},
        isDark: false,
        supportPhone: '',
        supportEmail: '',
        websiteUrl: '',
        brokerAddress: '',
        legalName: '',
        mobileAppLinks: { ios: '', android: '', is_enabled: false },
    });

    const fetchBrokerConfig = (organisation_uuid?: string) => {
        const org_uuid = organisation_uuid || userData?.organisation_uuid;
        setLoading(true);

        api.broker
            .getBrokerConfig({ organisation_uuid: org_uuid })
            .then((response) => {
                dispatch(
                    themeActions.setPaymentEnforcementConfig({
                        temporaryAppAccessSuspension:
                            response?.data?.payment_enforcement_config?.temporary_app_access_suspension === true,
                    })
                );

                if (organisation_uuid) {
                    dispatch(dataActions.setHasORPAccess({
                        hasORPAccess: response?.data?.organisation_config?.allow_orp,
                        hasEvenQuicklnks: response?.data?.organisation_config?.allow_even_quicklinks,
                        hasGiftCards: response?.data?.organisation_config?.allow_gift_cards,
                    }));
                }

                // setSsoUrl(response?.data?.sso_url);

                if (isSQ) {
                    dispatch(
                        themeActions.setTheme({
                            primaryColor: '#0FA3B1',
                            logoUrl: response?.data?.organisation_config?.logo_url
                                ? response?.data?.organisation_config?.logo_url
                                : response?.data?.logo_url,
                            brandName: response?.data?.organisation_config?.brand_name,
                            loginOptions: response?.data?.login_services || {},
                            isDark: response?.data?.organisation_config?.is_dark,
                            brokerName: response?.data?.brand_name,
                            description: response?.data?.addition_details?.tagline || `Employee Benefits Platform`,
                            supportPhone: response?.data?.support_phone || '',
                            supportEmail: response?.data?.support_email || '',
                            websiteUrl: response?.data?.addition_details?.client?.website_url || '',
                            brokerAddress: response?.data?.address || '',
                            legalName: response?.data?.legal_name || '',
                            mobileAppLinks: response?.data?.mobile_app_links || response?.data?.addition_details?.mobile_app_links || { ios: '', android: '', is_enabled: false },
                            ssoUrl: response?.data?.sso_url
                        })
                    );
                    setTheme({
                        primaryColor: '#0FA3B1',
                        logoUrl: response?.data?.organisation_config?.logo_url
                            ? response?.data?.organisation_config?.logo_url
                            : response?.data?.logo_url,
                        favicon: response?.data?.favicon_url,
                        title: response?.data?.brand_name,
                        description: response?.data?.addition_details?.tagline || `Employee Benefits Platform`,
                        loginOptions: response?.data?.login_services || {},
                        isDark: response?.data?.organisation_config?.is_dark,
                        supportPhone: response?.data?.support_phone || '',
                        supportEmail: response?.data?.support_email || '',
                        websiteUrl: response?.data?.addition_details?.client?.website_url || '',
                        brokerAddress: response?.data?.address || '',
                        legalName: response?.data?.legal_name || '',
                        mobileAppLinks: response?.data?.mobile_app_links || response?.data?.addition_details?.mobile_app_links || { ios: '', android: '', is_enabled: false },
                    });
                } else {
                    dispatch(
                        themeActions.setTheme({
                            primaryColor: response?.data?.organisation_config?.theme_color
                                ? response?.data?.organisation_config?.theme_color
                                : response?.data?.theme_color,
                            logoUrl: response?.data?.organisation_config?.logo_url
                                ? response?.data?.organisation_config?.logo_url
                                : response?.data?.logo_url,
                            brandName: response?.data?.organisation_config?.brand_name,
                            loginOptions: response?.data?.login_services || {},
                            isDark: response?.data?.organisation_config?.is_dark,
                            brokerName: response?.data?.brand_name,
                            description: response?.data?.addition_details?.tagline || `Employee Benefits Platform`,
                            supportPhone: response?.data?.support_phone || '',
                            supportEmail: response?.data?.support_email || '',
                            websiteUrl: response?.data?.addition_details?.client?.website_url || '',
                            brokerAddress: response?.data?.address || '',
                            legalName: response?.data?.legal_name || '',
                            mobileAppLinks: response?.data?.mobile_app_links || response?.data?.addition_details?.mobile_app_links || { ios: '', android: '', is_enabled: false },
                            ssoUrl: response?.data?.sso_url
                        })
                    );
                    setTheme({
                        primaryColor: response?.data?.organisation_config?.theme_color
                            ? response?.data?.organisation_config?.theme_color
                            : response?.data?.theme_color,
                        logoUrl: response?.data?.organisation_config?.logo_url
                            ? response?.data?.organisation_config?.logo_url
                            : response?.data?.logo_url,
                        favicon: response?.data?.favicon_url,
                        title: response?.data?.brand_name,
                        description: response?.data?.addition_details?.tagline || `Employee Benefits Platform`,
                        loginOptions: response?.data?.login_services || {},
                        isDark: response?.data?.organisation_config?.is_dark,
                        supportPhone: response?.data?.support_phone || '',
                        supportEmail: response?.data?.support_email || '',
                        websiteUrl: response?.data?.addition_details?.client?.website_url || '',
                        brokerAddress: response?.data?.address || '',
                        legalName: response?.data?.legal_name || '',
                        mobileAppLinks: response?.data?.mobile_app_links || response?.data?.addition_details?.mobile_app_links || { ios: '', android: '', is_enabled: false },
                    });
                }

                dispatch(
                    flagActions.setAutoInvitationDays({
                        invitation_auto_accept_days: response?.data?.organisation_config?.invitation_auto_accept_days,
                    })
                );
                dispatch(
                    flagActions.setIsNomineeManagementEnabled({
                        is_nominee_management_enabled: response?.data?.organisation_config?.nominee_addition?.can_employee_add_nominee,
                    })
                );

                if (response?.data?.addition_details?.client?.google_analytics) {
                    ReactGA.initialize(response?.data?.addition_details?.client?.google_analytics);
                }
                if (response?.data?.client?.google_analytics) {
                    ReactGA.initialize(response?.data?.client?.google_analytics);
                }
                if (response?.data?.addition_details?.client?.mixpanel) {
                    setLocalData('mixpanel', response?.data?.addition_details?.client?.mixpanel);
                }
                if (response?.data?.client?.mixpanel) {
                    setLocalData('mixpanel', response?.data?.client?.mixpanel);
                }
                if (response?.data?.addition_details?.client?.custom_script_urls) {
                    setCustomUrls(response?.data?.addition_details?.client?.custom_script_urls?.split(','));
                    response?.data?.addition_details?.client?.custom_script_urls?.split(',').map((url: string) => {
                        var script = document.createElement('script');
                        script.type = 'text/javascript';
                        script.src = url;
                        document.head.appendChild(script);
                    });
                }
                if (response?.data?.client?.custom_script_urls) {
                    setCustomUrls(response?.data?.client?.custom_script_urls?.split(','));
                    response?.data?.client?.custom_script_urls?.split(',').map((url: string) => {
                        var script = document.createElement('script');
                        script.type = 'text/javascript';
                        script.src = url;
                        document.head.appendChild(script);
                    });
                }
                if (response?.data?.addition_details?.client?.custom_code) {
                    var script = document.createElement('script');
                    script.type = 'text/javascript';
                    script.innerHTML = response?.data?.addition_details?.client?.custom_code;
                    document.head.appendChild(script);
                }
                if (response?.data?.client?.custom_code) {
                    var script = document.createElement('script');
                    script.type = 'text/javascript';
                    script.innerHTML = response?.data?.client?.custom_code;
                    document.head.appendChild(script);
                }
            })
            .catch((error: any) => {
                errorHandler(error?.response?.data);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return { loading, theme, customUrls, fetchBrokerConfig };
};

export default useBrokerConfig;
